/**
 * Custom element that renders a scrollbar progress indicator for a slideshow.
 * Syncs thumb position/width with the parent slideshow-component's scroller.
 * Supports drag-to-scroll on the thumb and click-to-seek on the track.
 */
class SlideshowScrollbar extends HTMLElement {
  /** @type {HTMLElement | null} */
  #scroller = null;
  /** @type {ResizeObserver | null} */
  #resizeObserver = null;
  /** @type {boolean} */
  #dragging = false;

  connectedCallback() {
    const scrollerSelector = this.getAttribute('scroller');

    if (scrollerSelector) {
      this.#scroller = this.parentElement?.querySelector(scrollerSelector) || null;
    } else {
      const slideshowComponent = this.closest('slideshow-component');
      if (!slideshowComponent) return;
      this.#scroller = slideshowComponent.querySelector('slideshow-slides[ref="scroller"]');
    }

    if (!this.#scroller) return;

    this.#scroller.addEventListener('scroll', this.#onScroll, { passive: true });

    this.#resizeObserver = new ResizeObserver(() => this.#update());
    this.#resizeObserver.observe(this.#scroller);

    const track = this.querySelector('[ref="scrollbarTrack"]');
    if (track) {
      track.addEventListener('pointerdown', this.#onTrackPointerDown);
    }

    this.#update();
  }

  disconnectedCallback() {
    if (this.#scroller) {
      this.#scroller.removeEventListener('scroll', this.#onScroll);
    }
    if (this.#resizeObserver) {
      this.#resizeObserver.disconnect();
      this.#resizeObserver = null;
    }
    this.#scroller = null;
  }

  #onScroll = () => {
    if (!this.#dragging) this.#update();
  };

  #update() {
    const scroller = this.#scroller;
    if (!scroller) return;

    const { scrollWidth, clientWidth, scrollLeft } = scroller;

    // Hide if all content is visible
    if (scrollWidth <= clientWidth) {
      this.hidden = true;
      return;
    }
    this.hidden = false;

    const track = this.querySelector('[ref="scrollbarTrack"]');
    const thumb = this.querySelector('[ref="scrollbarThumb"]');
    if (!track || !thumb) return;

    const trackWidth = track.clientWidth;
    const thumbWidth = Math.max((clientWidth / scrollWidth) * trackWidth, 24);
    const maxThumbLeft = trackWidth - thumbWidth;
    const scrollRange = scrollWidth - clientWidth;
    const thumbLeft = scrollRange > 0 ? (scrollLeft / scrollRange) * maxThumbLeft : 0;

    thumb.style.width = `${thumbWidth}px`;
    thumb.style.transform = `translateX(${thumbLeft}px)`;

    // Update arrow disabled states
    const prevBtn = this.querySelector('[ref="previous"]');
    const nextBtn = this.querySelector('[ref="next"]');
    if (prevBtn) prevBtn.disabled = scrollLeft <= 0;
    if (nextBtn) nextBtn.disabled = scrollLeft >= scrollRange - 1;
  }

  /** @param {PointerEvent} e */
  #onTrackPointerDown = (e) => {
    e.preventDefault();
    const scroller = this.#scroller;
    const track = this.querySelector('[ref="scrollbarTrack"]');
    const thumb = this.querySelector('[ref="scrollbarThumb"]');
    if (!scroller || !track || !thumb) return;

    const trackRect = track.getBoundingClientRect();
    const thumbRect = thumb.getBoundingClientRect();

    // Check if pointer is on the thumb
    if (e.clientX >= thumbRect.left && e.clientX <= thumbRect.right) {
      this.#startDrag(e, track, thumb, scroller);
      return;
    }

    // Click on track — scroll to position
    const clickRatio = (e.clientX - trackRect.left) / trackRect.width;
    const scrollRange = scroller.scrollWidth - scroller.clientWidth;
    scroller.scrollTo({ left: clickRatio * scrollRange, behavior: 'smooth' });
  };

  /**
   * @param {PointerEvent} startEvent
   * @param {Element} track
   * @param {Element} thumb
   * @param {HTMLElement} scroller
   */
  #startDrag(startEvent, track, thumb, scroller) {
    this.#dragging = true;
    const trackWidth = track.clientWidth;
    const thumbWidth = thumb.clientWidth;
    const maxThumbLeft = trackWidth - thumbWidth;
    const scrollRange = scroller.scrollWidth - scroller.clientWidth;
    const startX = startEvent.clientX;
    const startThumbLeft = parseFloat(thumb.style.transform.replace(/[^-\d.]/g, '')) || 0;

    const controller = new AbortController();
    const { signal } = controller;

    /** @param {PointerEvent} e */
    const onMove = (e) => {
      const delta = e.clientX - startX;
      const newThumbLeft = Math.max(0, Math.min(maxThumbLeft, startThumbLeft + delta));
      const scrollRatio = maxThumbLeft > 0 ? newThumbLeft / maxThumbLeft : 0;

      thumb.style.transform = `translateX(${newThumbLeft}px)`;
      scroller.scrollLeft = scrollRatio * scrollRange;
    };

    const onUp = () => {
      this.#dragging = false;
      controller.abort();
      this.#update();
    };

    document.addEventListener('pointermove', onMove, { signal });
    document.addEventListener('pointerup', onUp, { signal });
    document.addEventListener('pointercancel', onUp, { signal });
  }
}

if (!customElements.get('slideshow-scrollbar')) {
  customElements.define('slideshow-scrollbar', SlideshowScrollbar);
}
